import React from 'react';
import { Link } from 'react-router-dom';
import { PopupButton } from 'react-calendly';
import { FaCheck, FaArrowRight, FaStar, FaRobot, FaVideo, FaPodcast, FaSearch, FaEnvelope, FaGlobe, FaCode, FaWhatsapp, FaPhoneAlt, FaDatabase } from 'react-icons/fa';

const packages = [
  {
    name: 'Advanced Lead Generation Suite',
    icon: <FaDatabase />,
    price: '',
    period: '',
    description: 'Multi-platform lead generation and contact discovery system',
    features: [
      'Website & Social Media Scraping',
      'Email & Contact Discovery',
      'Location-Based Lead Generation',
      'Business Profile Analysis',
      'Automated Lead Qualification',
      'Weekly Lead Reports'
    ],
    popular: false
  },
  {
    name: 'WhatsApp & Instagram Chatbot',
    icon: <FaWhatsapp />,
    price: '',
    period: '',
    description: 'Intelligent chatbot integration for WhatsApp and Instagram',
    features: [
      'Multi-Platform Chatbot Integration',
      'Natural Language Processing',
      'Automated Customer Support',
      'Lead Generation & Qualification',
      'Custom Response Templates',
      'Analytics Dashboard'
    ],
    popular: false
  },
  {
    name: 'AI Cold Call Assistant',
    icon: <FaPhoneAlt />,
    price: '',
    period: '',
    description: 'Premium AI-powered cold calling automation system with advanced features',
    features: [
      'Enterprise-Grade Voice AI',
      'Unlimited Multi-Language Support',
      'Advanced Call Routing & Scheduling',
      'Deep Learning Voice Recognition',
      'Enterprise Analytics Suite',
      'Priority Support & Training'
    ],
    popular: true
  },
  {
    name: 'Social Media AI Package',
    icon: <FaRobot />,
    price: '',
    period: '',
    description: 'Comprehensive AI-powered social media management and content generation',
    features: [
      'AI Content Generation for Multiple Platforms',
      'Automated Posting Schedule',
      'Engagement Analytics',
      'Sentiment Analysis',
      'Trend Detection',
      'Performance Reports'
    ],
    popular: false
  },
  {
    name: 'Video & Podcast Production',
    icon: <FaVideo />,
    price: '',
    period: '',
    description: 'Professional AI-assisted video and audio content creation',
    features: [
      'AI Video Script Generation',
      'Automated Video Editing',
      'Podcast Content Creation',
      'Voice Synthesis',
      'Subtitle Generation',
      'Content Distribution'
    ],
    popular: false
  },
  {
    name: 'Website & SEO Optimization',
    icon: <FaSearch />,
    price: '',
    period: '',
    description: 'AI-driven website optimization and SEO enhancement',
    features: [
      'AI SEO Analysis',
      'Content Optimization',
      'Keyword Research',
      'Performance Monitoring',
      'Competitor Analysis',
      'Monthly Reports'
    ],
    popular: false
  },
  {
    name: 'Email Marketing Automation',
    icon: <FaEnvelope />,
    price: '',
    period: '',
    description: 'Smart email marketing with AI-powered automation',
    features: [
      'AI Email Content Generation',
      'Automated Campaigns',
      'A/B Testing',
      'Audience Segmentation',
      'Performance Analytics',
      'Lead Scoring'
    ],
    popular: false
  },
  {
    name: 'Website Automation',
    icon: <FaGlobe />,
    price: '',
    period: '',
    description: 'Complete website automation and optimization solution',
    features: [
      'AI Chatbot Integration',
      'Form Automation',
      'User Journey Optimization',
      'Conversion Tracking',
      'Performance Optimization',
      'Security Enhancement'
    ],
    popular: false
  },
  {
    name: 'Custom AI Development',
    icon: <FaCode />,
    price: '',
    period: '',
    description: 'Tailored AI solutions development and integration',
    features: [
      'Custom AI Model Development',
      'API Integration',
      'System Architecture',
      'Testing & Deployment',
      'Maintenance & Support',
      'Documentation'
    ],
    popular: false
  }
];

const Packages = () => {
  return (
    <div className="min-h-screen bg-nvidia-dark py-20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our AI Service Packages
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Choose from our comprehensive range of AI-powered solutions designed to transform your business
          </p>
          
          <div className="max-w-4xl mx-auto text-left space-y-6 mb-16">
            <p className="text-lg text-gray-300">
              Welcome to Botrex AI, where we transform businesses through cutting-edge artificial intelligence solutions. Our carefully curated packages combine the latest AI technologies with human expertise to deliver exceptional results.
            </p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Advanced Lead Generation Suite - Multi-Platform Lead Discovery</h3>
                <p className="text-gray-400">Unlock powerful lead generation with our comprehensive data extraction system. Our AI-powered platform intelligently scrapes and analyzes data from websites, Instagram, TikTok, Google Maps, and LinkedIn to discover valuable leads. We ethically collect contact information, email addresses, and location data, then qualify and organize them based on your business needs. Perfect for businesses looking to expand their reach and find highly targeted prospects across multiple platforms.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Social Media AI Package - Your Digital Voice, Amplified</h3>
                <p className="text-gray-400">Perfect for businesses looking to maintain a strong, consistent social media presence. Our AI analyzes your brand voice and automatically generates engaging content while maintaining your authentic tone. We handle everything from content creation to performance analytics, letting you focus on what matters most - your core business.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Video & Podcast Production - Professional Content Creation Made Simple</h3>
                <p className="text-gray-400">Transform your ideas into professional video and audio content with our AI-powered production suite. From script writing to final editing, our system streamlines the entire production process while maintaining high quality standards. Perfect for businesses looking to expand their content marketing strategy.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Website & SEO Optimization - Maximize Your Digital Impact</h3>
                <p className="text-gray-400">Our AI-driven SEO solution continuously analyzes and optimizes your website's performance. We don't just focus on keywords - our comprehensive approach considers user experience, content quality, and technical optimization to ensure your website ranks higher and converts better.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Email Marketing Automation - Personalized Communication at Scale</h3>
                <p className="text-gray-400">Leverage the power of AI to create personalized email campaigns that resonate with your audience. Our system learns from recipient behavior to optimize content, sending times, and segmentation, ensuring higher engagement rates and better ROI for your email marketing efforts.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Website Automation - Your Website, Working Smarter</h3>
                <p className="text-gray-400">Enhance your website with intelligent automation that improves user experience and increases conversions. From smart chatbots that handle customer queries to automated form processing and user journey optimization, we make your website work harder for your business.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">Custom AI Development - Tailored Solutions for Unique Challenges</h3>
                <p className="text-gray-400">For businesses with specific needs, our custom AI development service creates bespoke solutions that address your unique challenges. Our team of experts works closely with you to understand your requirements and develop AI solutions that integrate seamlessly with your existing systems.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">WhatsApp & Instagram Chatbot - Smart Conversations Across Platforms</h3>
                <p className="text-gray-400">Elevate your customer engagement with our intelligent chatbot solution for WhatsApp and Instagram. Our AI-powered system handles inquiries 24/7, understands natural language, and maintains personalized conversations across both platforms. Perfect for businesses looking to provide instant support, qualify leads, and reduce response times while maintaining a personal touch.</p>
              </div>

              <div>
                <h3 className="text-nvidia-green font-semibold mb-2">AI Cold Call Assistant - Enterprise-Grade Conversation Intelligence</h3>
                <p className="text-gray-400">Our flagship AI Cold Call Assistant represents the pinnacle of sales automation. Powered by enterprise-grade AI and deep learning, this premium solution delivers unparalleled natural conversations, handles complex sales interactions, and scales your outreach efforts to meet enterprise-level demands. With unlimited language support, advanced analytics, and priority support, it's the ultimate solution for businesses looking to revolutionize their cold calling strategy.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`relative p-8 rounded-lg bg-white border ${
                pkg.popular ? 'border-nvidia-green' : 'border-gray-200'
              } transform hover:-translate-y-1 transition-transform duration-300`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-nvidia-green text-black px-4 py-1 rounded-full text-sm font-semibold flex items-center">
                    <FaStar className="mr-1" /> Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-nvidia-green text-3xl mb-4">
                {pkg.icon}
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{pkg.name}</h3>
              <p className="text-gray-600 mb-4">{pkg.description}</p>
              
              <div className="mb-6">
                <span className="text-3xl font-bold text-gray-900">{pkg.price}</span>
                <span className="text-gray-600">{pkg.period}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <FaCheck className="text-nvidia-green mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Get Started button removed */}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-xl text-gray-400 mb-6">
            Need a custom solution? We've got you covered.
          </p>
          <PopupButton
            url="https://calendly.com/rahwsty/30min"
            rootElement={document.getElementById('root')}
            text="Contact Us for Custom Solutions"
            className="inline-flex items-center bg-white text-nvidia-dark px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
          />
        </div>
      </div>
    </div>
  );
};

export default Packages;
